/**
 * Rule context — per-page variable provider.
 *
 * Each page that hosts configurable components wraps its body in a
 * `<RuleContextProvider>` so every nested HOC can read the same bag of
 * rule variables (`task_type`, `claim_amount`, etc.) via
 * {@link useRuleContext}.
 *
 * The provider also performs the tenant-wide inactive-rules fetch via
 * {@link useInactiveComponentRules} and exposes the resulting map on a
 * sibling React context so each HOC can filter out suppressed rule ids
 * without issuing N React Query subscriptions.
 *
 * The underlying `RuleContext` / `InactiveRulesContext` constants and
 * their consumer hooks live in sibling `.ts` files; this file only
 * exports the provider component so Vite's react-refresh plugin stays
 * happy.
 */

import { useMemo, type ReactNode } from 'react';

import {
  InactiveRulesContext,
  InactiveRulesReadyContext,
} from './inactiveRulesContext';
import { RuleContext } from './ruleContext';
import type { RuleContextValue } from './types';
import { useInactiveComponentRules } from './useInactiveComponentRules';

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Props accepted by {@link RuleContextProvider}.
 */
export interface RuleContextProviderProps {
  /**
   * The variable bag exposed to every descendant HOC. Callers should
   * memoise this value on the page's primitive deps so rule evaluation
   * does not re-run on unrelated re-renders.
   */
  value: RuleContextValue;
  /** Page body — typically the entire render tree under the route element. */
  children: ReactNode;
}

/**
 * Wrap a page subtree in a rule context bag.
 *
 * Usage:
 * ```tsx
 * const ruleContextValue = useMemo(
 *   () => ({ task_type: taskInfo?.ECRAN }),
 *   [taskInfo?.ECRAN],
 * );
 *
 * return (
 *   <RuleContextProvider value={ruleContextValue}>
 *     {pageBody}
 *   </RuleContextProvider>
 * );
 * ```
 */
export function RuleContextProvider({
  value,
  children,
}: Readonly<RuleContextProviderProps>): ReactNode {
  const { inactiveRuleIdsByComponent, isLoading } = useInactiveComponentRules();

  // React Query's `select` already produces a stable reference, but we
  // double-guard with `useMemo` so a fresh provider-level render does
  // not create a new map identity on every tick.
  const inactiveMap = useMemo(
    () => inactiveRuleIdsByComponent,
    [inactiveRuleIdsByComponent],
  );

  return (
    <RuleContext.Provider value={value}>
      <InactiveRulesContext.Provider value={inactiveMap}>
        <InactiveRulesReadyContext.Provider value={!isLoading}>
          {children}
        </InactiveRulesReadyContext.Provider>
      </InactiveRulesContext.Provider>
    </RuleContext.Provider>
  );
}
